import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getLocationTaxRates } from '../api/taxRates.api';
import { queryKeys } from '../api/queryKeys';
import type { LocationTaxRateEntity } from '../types/taxRate.types';

const NO_TAX_RATES: LocationTaxRateEntity[] = [];

/**
 * The tax rates of one location.
 *
 * Shared by the admin location screen (which edits them) and by the menu's
 * item and option pickers (which only pick from them), so both read the same
 * cache entry - assigning a tax on an item and then opening the location
 * screen shows the same list without a second request.
 *
 * `enabled` is how the pickers avoid fetching until a modal actually opens.
 */
export function useLocationTaxRates(
  locationId: number | string | null | undefined,
  options: { enabled?: boolean } = {}
) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.taxRates.byLocation(locationId ?? ''),
    queryFn: async () => {
      const { data } = await getLocationTaxRates(locationId as number | string);
      return data.data;
    },
    enabled: enabled && locationId != null && locationId !== '',
    // Rates change rarely and every write here invalidates them, so a minute
    // of freshness is enough to keep reopening a picker from refetching.
    staleTime: 60 * 1000,
  });

  return {
    /** Always an array - empty until the first response. */
    taxRates: query.data ?? NO_TAX_RATES,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    /** Call after any write that changes the list. */
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.taxRates.byLocation(locationId ?? ''),
      }),
  };
}
